import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI, kycAPI, technicalAnalysisAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Loader2, Plus, TrendingUp, TrendingDown, 
  Home, Wallet, BarChart3, Award, FileText, HelpCircle, Settings,
  ChevronRight, Search, Activity, User, LogOut, Shield, AlertTriangle,
  DollarSign, Target, CheckCircle, XCircle, Eye, Users, Bell, Key, Copy, Check, ExternalLink,
  Coins, Lock, ArrowRight
} from 'lucide-react';
import FloatingLossIndicator from '../components/FloatingLossIndicator';
import NotificationBell from '../components/NotificationBell';
import MatchTraderCredentials from '../components/MatchTraderCredentials';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Navigation Component
const Sidebar = ({ activeItem, onNavigate, user, onLogout }) => {
  const navItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/dashboard' },
    { id: 'kbtcoin', label: 'KBTCOIN', icon: Coins, path: '/kbtcoin' },
    { id: 'withdrawal', label: 'Withdrawal', icon: Wallet, path: '/payouts' },
    { id: 'stats', label: 'My Stats', icon: BarChart3, path: '/stats' },
    { id: 'certificates', label: 'Certificates', icon: Award, path: '/certificates' },
    { id: 'affiliates', label: 'Affiliates', icon: Users, path: '/affiliates' },
    { id: 'signals', label: 'Market Signals', icon: Activity, path: '/analysis' },
    { id: 'rules', label: 'Rules & FAQ', icon: HelpCircle, path: '/faq' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  ];

  // Add Admin Panel for admin users
  const adminItems = user?.is_admin ? [
    { id: 'admin', label: 'Admin Panel', icon: Shield, path: '/admin', isAdmin: true },
  ] : [];

  return (
    <div className="fixed left-0 top-0 h-full w-64 bg-[hsl(220,25%,8%)] border-r border-white/10 z-40 flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="TRADERSCAPITAL" className="h-10 w-auto" />
          <span className="font-heading font-bold text-white text-lg">TRADERS<span className="text-cyan-400">CAPITAL</span></span>
        </div>
      </div>

      {/* Admin Panel Button - Only for admins */}
      {user?.is_admin && (
        <div className="p-4 border-b border-white/10">
          <Button 
            onClick={() => onNavigate('/admin')}
            className="w-full font-heading uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg shadow-lg shadow-amber-500/25"
            data-testid="admin-panel-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            Admin CRM
          </Button>
        </div>
      )}

      {/* New Challenge Button */}
      <div className="p-4">
        <Button 
          onClick={() => onNavigate('/challenges')}
          className="w-full font-heading uppercase tracking-wider bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-lg shadow-lg shadow-blue-500/25"
          data-testid="new-challenge-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Challenge
        </Button>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 px-3 py-2 overflow-y-auto">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.path)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all ${
              activeItem === item.id 
                ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name || 'Trader'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onLogout}
          className="w-full justify-start text-gray-400 hover:text-red-400 hover:bg-red-500/10"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

// Mobile Sidebar Toggle
const MobileSidebar = ({ isOpen, onClose, activeItem, onNavigate, user, onLogout }) => {
  if (!isOpen) return null;
  
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      <div className="fixed left-0 top-0 h-full w-64 bg-[hsl(220,25%,8%)] border-r border-white/10 z-50 lg:hidden">
        <Sidebar activeItem={activeItem} onNavigate={(path) => { onNavigate(path); onClose(); }} user={user} onLogout={onLogout} />
      </div>
    </>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
  };

  return (
    <Card className="bg-[hsl(220,20%,7%)] border-white/10 hover:border-white/20 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-400 mb-1">{title}</p>
            <p className="text-2xl font-bold text-white font-mono">{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Account Card Component (Aquafunded Style)
const AccountCard = ({ account, onViewDashboard, onDownloadCert, downloadingCert }) => {
  const getStatusBadge = (status) => {
    switch (status) {
      case 'funded':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-medium">FUNDED</Badge>;
      case 'active':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 font-medium">ACTIVE</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 font-medium">FAILED</Badge>;
      case 'passed':
      case 'passed_phase1':
        return <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 font-medium">PASSED</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 font-medium">{status?.toUpperCase()}</Badge>;
    }
  };

  const getPhaseText = (phase, packageType) => {
    if (packageType === 'instant') return 'Instant Funding';
    if (packageType === 'demo') return 'Demo Account';
    return `Phase ${phase}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[hsl(220,20%,7%)] border border-white/10 rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all"
      data-testid={`account-card-${account.id}`}
    >
      {/* Card Header */}
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <p className="font-heading font-bold text-white">{account.package_name}</p>
            <p className="text-xs text-gray-500">
              {getPhaseText(account.current_phase, account.package_type)} • Started {formatDate(account.start_date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs text-gray-400 border-gray-600">
            {account.profit_split || 80}% Split
          </Badge>
          {getStatusBadge(account.status)}
        </div>
      </div>

      {/* Card Body - Financial Stats */}
      <div className="p-5">
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Starting Balance</p>
            <p className="text-lg font-mono font-bold text-white">{formatCurrency(account.starting_balance)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Equity</p>
            <p className={`text-lg font-mono font-bold ${account.current_balance >= account.starting_balance ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(account.current_balance)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Balance</p>
            <p className="text-lg font-mono font-bold text-white">{formatCurrency(account.current_balance)}</p>
          </div>
        </div>

        {/* Profit/Loss Indicator */}
        <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/5 mb-4">
          <div className="flex items-center gap-2">
            {account.profit_loss >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-400" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-400" />
            )}
            <span className="text-sm text-gray-400">Profit/Loss</span>
          </div>
          <span className={`font-mono font-bold ${account.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {account.profit_loss >= 0 ? '+' : ''}{formatCurrency(account.profit_loss)} ({account.profit_loss_percent?.toFixed(2) || '0.00'}%)
          </span>
        </div>

        {/* Floating Loss Indicator - 2% Rule */}
        {(account.status === 'active' || account.status === 'funded' || account.status === 'passed_phase1') && (
          <div className="mb-4">
            <FloatingLossIndicator 
              floatingLossPercent={account.floating_loss_percent || 0}
              maxFloatingLoss={account.max_floating_loss || 2}
              size="small"
            />
          </div>
        )}

        {/* Trading Days */}
        <div className="flex items-center justify-between text-sm mb-4">
          <span className="text-gray-400">Trading Days</span>
          <span className="font-mono text-white">{account.trading_days || 0} days</span>
        </div>

        {/* Match-Trader Credentials */}
        {account.matchtrade_login && (
          <div className="mb-4 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-medium text-white">Match-Trader Credentials</span>
              </div>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                Active
              </Badge>
            </div>
            
            {/* Login ID */}
            <div className="flex items-center justify-between bg-black/30 rounded px-3 py-2 mb-2">
              <div>
                <span className="text-xs text-gray-500 block">Login ID</span>
                <span className="font-mono text-white font-bold">{account.matchtrade_login}</span>
              </div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(account.matchtrade_login);
                  toast.success('Login ID copied!');
                }}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            {/* Password */}
            <div className="flex items-center justify-between bg-black/30 rounded px-3 py-2 mb-3">
              <div>
                <span className="text-xs text-gray-500 block">Password</span>
                <span className="font-mono text-white font-bold">{account.matchtrade_password || '••••••••'}</span>
              </div>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(account.matchtrade_password);
                  toast.success('Password copied!');
                }}
                className="p-1.5 hover:bg-white/10 rounded transition-colors"
              >
                <Copy className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            {/* Open Platform Button */}
            <a
              href="https://demo.match-trader.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded py-2 px-3 text-sm font-medium transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Open Match-Trader Platform
            </a>
            
            <p className="text-xs text-gray-500 mt-2 text-center">
              Use your email + password above to login
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={() => onViewDashboard(account.id)}
            className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-heading uppercase text-sm"
            data-testid={`view-dashboard-${account.id}`}
          >
            <Eye className="w-4 h-4 mr-2" />
            View Dashboard
          </Button>
          {account.status === 'funded' && (
            <Button 
              variant="outline"
              onClick={() => onDownloadCert(account.id)}
              disabled={downloadingCert === account.id}
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
              data-testid={`download-cert-${account.id}`}
            >
              {downloadingCert === account.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Award className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [challenges, setChallenges] = useState([]);
  const [kycStatus, setKycStatus] = useState(null);
  const [taSignals, setTaSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, kycRes, taRes, tokenRes] = await Promise.all([
        challengesAPI.getMyChallenges(),
        kycAPI.getStatus(),
        technicalAnalysisAPI.getAllSummary().catch(() => ({ data: { instruments: [] } })),
        fetch(`${API_URL}/api/token/balance`, { 
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
        }).then(r => r.ok ? r.json() : null).catch(() => null)
      ]);
      setChallenges(challengesRes.data);
      setKycStatus(kycRes.data);
      setTaSignals(taRes.data?.instruments?.slice(0, 6) || []);
      setTokenBalance(tokenRes);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (challengeId) => {
    setDownloadingCert(challengeId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/certificates/${challengeId}/download?format=pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download certificate');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TRADERSCAPITAL_Certificate.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    } finally {
      setDownloadingCert(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Filter accounts based on search
  const filteredAccounts = challenges.filter(c => 
    c.package_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const totalPayouts = challenges.reduce((sum, c) => sum + (c.total_withdrawn || 0), 0);
  const totalAccounts = challenges.length;
  const passedAccounts = challenges.filter(c => c.status === 'passed' || c.status === 'passed_phase1' || c.status === 'funded').length;
  const fundedAccounts = challenges.filter(c => c.status === 'funded').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)]" data-testid="dashboard">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar 
          activeItem="home" 
          onNavigate={navigate} 
          user={user} 
          onLogout={handleLogout}
        />
      </div>

      {/* Mobile Sidebar */}
      <MobileSidebar 
        isOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        activeItem="home"
        onNavigate={navigate}
        user={user}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[hsl(220,20%,7%)] border-b border-white/10 z-30 flex items-center justify-between px-4">
          <button 
            onClick={() => setMobileSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <img src="/logo.png" alt="TRADERSCAPITAL" className="h-8" />
          <NotificationBell />
        </div>

        <div className="pt-4 lg:pt-8 px-4 lg:px-8 pb-8">
          {/* Desktop Header with Notification Bell */}
          <div className="hidden lg:flex justify-end items-center gap-4 mb-4">
            <div className="flex items-center gap-3">
              <NotificationBell />
              <div className="h-8 w-px bg-white/10"></div>
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.full_name || 'Trader'}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 mt-16 lg:mt-0"
          >
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Welcome Back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">{user?.full_name?.split(' ')[0] || 'Trader'}</span>
            </h1>
            <p className="text-gray-400">Manage your trading accounts and track your performance</p>
          </motion.div>

          {/* KYC Verification Alert */}
          {kycStatus && kycStatus.status !== 'verified' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-6"
            >
              <div className={`rounded-xl p-4 flex items-center justify-between ${
                kycStatus.status === 'pending' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                kycStatus.status === 'submitted' ? 'bg-blue-500/10 border border-blue-500/30' :
                'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className="flex items-center gap-3">
                  {kycStatus.status === 'pending' && <AlertTriangle className="w-5 h-5 text-yellow-400" />}
                  {kycStatus.status === 'submitted' && <Shield className="w-5 h-5 text-blue-400" />}
                  {kycStatus.status === 'rejected' && <AlertTriangle className="w-5 h-5 text-red-400" />}
                  <div>
                    <p className="font-medium text-white">
                      {kycStatus.status === 'pending' && 'Complete verification to request payouts from funded accounts'}
                      {kycStatus.status === 'submitted' && 'Verification Under Review (1-2 business days)'}
                      {kycStatus.status === 'rejected' && 'Verification Rejected - Please resubmit documents'}
                    </p>
                  </div>
                </div>
                {(kycStatus.status === 'pending' || kycStatus.status === 'rejected') && (
                  <Button 
                    onClick={() => navigate('/verification')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-heading uppercase text-sm"
                    data-testid="verify-btn"
                  >
                    Verify Now
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            <StatsCard title="Total Payouts" value={formatCurrency(totalPayouts)} icon={DollarSign} color="green" />
            <StatsCard title="Number of Accounts" value={totalAccounts} icon={Target} color="blue" />
            <StatsCard title="Accounts Passed" value={passedAccounts} icon={CheckCircle} color="cyan" />
            <StatsCard title="Funded Accounts" value={fundedAccounts} icon={Award} color="yellow" />
          </motion.div>

          {/* KBTCOIN Portfolio Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.17 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-yellow-500/5 to-orange-500/5 border-yellow-500/20 hover:border-yellow-500/40 transition-colors cursor-pointer" onClick={() => navigate('/kbtcoin')} data-testid="kbtcoin-widget">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/20">
                      <Coins className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-0.5">KBTCOIN Portfolio</p>
                      <div className="flex items-center gap-3">
                        <p className="text-2xl font-bold font-mono text-yellow-400">{(tokenBalance?.balance || 0).toLocaleString()} KBT</p>
                        {tokenBalance?.stakes?.length > 0 && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                            <Lock className="w-3 h-3 mr-1" /> {tokenBalance.stakes.length} Active Stake{tokenBalance.stakes.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Current Value</p>
                      <p className="text-lg font-mono font-bold text-white">£{(tokenBalance?.current_value_gbp || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-400">
                      <span className="text-sm font-medium hidden md:inline">Manage Tokens</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search Account (eg: 12345, Demo Account)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 bg-[hsl(220,20%,7%)] border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                data-testid="search-accounts"
              />
            </div>
          </motion.div>

          {/* Account Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-white">Your Accounts</h2>
              <span className="text-sm text-gray-400">{filteredAccounts.length} account(s)</span>
            </div>

            {filteredAccounts.length === 0 ? (
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gray-500" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2 text-white">No Accounts Yet</h3>
                  <p className="text-gray-500 mb-6">Start your journey to becoming a funded trader today.</p>
                  <Button 
                    onClick={() => navigate('/challenges')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 font-heading uppercase"
                  >
                    Get Your First Challenge
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {filteredAccounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    account={account}
                    onViewDashboard={(id) => navigate(`/challenge/${id}`)}
                    onDownloadCert={downloadCertificate}
                    downloadingCert={downloadingCert}
                  />
                ))}
              </div>
            )}
          </motion.div>

          {/* Market Signals Preview (Bottom) */}
          {taSignals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8"
            >
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-cyan-400" />
                      <h3 className="font-heading font-bold text-white">Market Signals</h3>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate('/analysis')}
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {taSignals.map((signal) => {
                      const getSignalColor = (s) => {
                        if (s === 'strong_buy' || s === 'buy') return 'text-green-400 border-green-500/30 bg-green-500/10';
                        if (s === 'strong_sell' || s === 'sell') return 'text-red-400 border-red-500/30 bg-red-500/10';
                        return 'text-gray-400 border-white/10 bg-white/5';
                      };
                      return (
                        <div 
                          key={signal.symbol}
                          className={`p-3 rounded-lg border text-center cursor-pointer transition-all hover:scale-105 ${getSignalColor(signal.overall_signal)}`}
                          onClick={() => navigate('/analysis')}
                        >
                          <p className="font-heading font-bold text-sm text-white">{signal.symbol}</p>
                          <p className="text-xs mt-1">{signal.overall_signal?.replace('_', ' ').toUpperCase() || 'NEUTRAL'}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
